import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, useLocation }               from 'react-router-dom';
import { useAuth }                                 from '../contexts/AuthContext';
import {
  getCategories, generateCategories, listParts,
  generateSubSystemParts, getStatuses, getConditions, patchPart,
  listImages, deleteImage, getUnknownPart, reassignImage, createPart,
} from '../api/parts';
import './PartsPage.css';

/* ── Transform flat category rows → nested structure ─────────────────────────
   Input row shape (from VEHICLE_categories GET):
     { categoryId, category, category_order, subCategoryId, sub_category,
       sub_category_order, source, part_count }
   Output:
     [ { categoryId, category, category_order, part_count (total),
         sub_categories: [{ subCategoryId, sub_category, sub_category_order, part_count }] } ]
*/
function buildHierarchy(rows) {
  const map = new Map();
  rows.forEach(r => {
    if (!map.has(r.categoryId)) {
      map.set(r.categoryId, {
        categoryId:     r.categoryId,
        category:       r.category,
        category_order: r.category_order,
        part_count:     0,          // summed from sub-categories below
        sub_categories: [],
      });
    }
    if (r.subCategoryId) {
      const cat    = map.get(r.categoryId);
      const count  = parseInt(r.part_count, 10) || 0;
      const already = cat.sub_categories.find(s => s.subCategoryId === r.subCategoryId);
      if (!already) {
        cat.sub_categories.push({
          subCategoryId:      r.subCategoryId,
          sub_category:       r.sub_category,
          sub_category_order: r.sub_category_order,
          part_count:         count,
        });
        cat.part_count += count;    // roll up to parent
      }
    }
  });
  const cats = Array.from(map.values());
  cats.sort((a, b) => a.category_order - b.category_order);
  cats.forEach(c => c.sub_categories.sort((a, b) => a.sub_category_order - b.sub_category_order));
  return cats;
}

/* ── Category icons — fallback to gear if unknown ────────────────────────── */
const CAT_ICONS = {
  'engine':        '🔧',
  'drivetrain':    '⚙️',
  'suspension':    '🛞',
  'brakes':        '🛑',
  'steering':      '🎡',
  'electrical':    '⚡',
  'exhaust':       '💨',
  'body':          '🚘',
  'trim':          '✨',
  'interior':      '💺',
  'wheels':        '🔩',
  'tires':         '🔩',
  'chassis':       '🏗️',
  'decals':        '🏷️',
  'tags':          '🏷️',
  'fuel':          '⛽',
  'cooling':       '🌡️',
  'ignition':      '🔌',
  'default':       '🔩',
};

function categoryIcon(name = '') {
  const lower = name.toLowerCase();
  for (const [key, icon] of Object.entries(CAT_ICONS)) {
    if (lower.includes(key)) return icon;
  }
  return CAT_ICONS.default;
}

/* ── Condition badge colors ──────────────────────────────────────────────── */
const CONDITION_COLORS = {
  'NOS':     '#2a7a2a',
  'NEW':     '#2a7a2a',
  'GOOD':    '#4a6a2a',
  'FAIR':    '#7a6a1a',
  'POOR':    '#7a3a1a',
  'UNKNOWN': '#3a3a3a',
};

/* ═══════════════════════════════════════════════════════════════════════════ */

export default function PartsPage() {
  const { user, signOut }  = useAuth();
  const navigate           = useNavigate();
  const location           = useLocation();

  /* vehicle from localStorage */
  const [vehicle, setVehicle] = useState(null);

  /* hierarchy data */
  const [hierarchy,    setHierarchy]    = useState([]);
  const [loadingCats,  setLoadingCats]  = useState(true);
  const [catError,     setCatError]     = useState('');

  /* drill-down state */
  const [selectedCat,    setSelectedCat]    = useState(null);
  const [selectedSubCat, setSelectedSubCat] = useState(null);

  /* parts at leaf level */
  const [parts,        setParts]        = useState([]);
  const [loadingParts, setLoadingParts] = useState(false);
  const [partsError,   setPartsError]   = useState('');

  /* AI generate categories modal */
  const [showAiModal,  setShowAiModal]  = useState(false);
  const [generating,   setGenerating]   = useState(false);
  const [genError,     setGenError]     = useState('');

  /* ref data */
  const [statuses,    setStatuses]    = useState([]);
  const [conditions,  setConditions]  = useState([]);

  /* AI generate parts (sub-system) modal */
  const [showPartsModal,   setShowPartsModal]   = useState(false);
  const [partsModalTarget, setPartsModalTarget] = useState(null);
  const [generatingParts,  setGeneratingParts]  = useState(false);
  const [genPartsError,    setGenPartsError]    = useState('');

  /* ── Unknown / unidentified photos view ────────────────────────── */
  const [viewingUnknown,  setViewingUnknown]  = useState(false);
  const [unknownPartId,   setUnknownPartId]   = useState(null);
  const [unknownImages,   setUnknownImages]   = useState([]);
  const [loadingUnknown,  setLoadingUnknown]  = useState(false);
  const [unknownError,    setUnknownError]    = useState('');

  /* simple lightbox for unknown images */
  const [lightboxUrl, setLightboxUrl] = useState(null);

  /* ── Assign modal ────────────────────────────────────────────────── */
  const [assignImage,      setAssignImage]      = useState(null); // full image obj
  const [assignCatId,      setAssignCatId]      = useState('');
  const [assignSubCatId,   setAssignSubCatId]   = useState('');
  const [assignParts,      setAssignParts]      = useState([]);
  const [assignSelectedId, setAssignSelectedId] = useState(null);
  const [loadingAssign,    setLoadingAssign]    = useState(false);
  const [assignBusy,       setAssignBusy]       = useState(false);

  /* ── Add Part form (inside assign modal) ────────────────────────── */
  const [showAddPart,    setShowAddPart]    = useState(false);
  const [newPartName,    setNewPartName]    = useState('');
  const [newPartOem,     setNewPartOem]     = useState('');
  const [newPartDesc,    setNewPartDesc]    = useState('');
  const [newPartCondId,  setNewPartCondId]  = useState('');
  const [newPartStatId,  setNewPartStatId]  = useState('');
  const [addPartBusy,    setAddPartBusy]    = useState(false);
  const [addPartError,   setAddPartError]   = useState('');

  /* ── Load vehicle from localStorage + ref data ──────────────────── */
  useEffect(() => {
    const cached = localStorage.getItem('ar_active_vehicle');
    if (cached) {
      try { setVehicle(JSON.parse(cached)); } catch (_) {}
    }
    getStatuses().then(r => setStatuses(r.data || [])).catch(() => {});
    getConditions().then(r => setConditions(r.data || [])).catch(() => {});

    // Restore drill-down position when returning from a part detail page
    const st = location.state;
    if (st?.selectedCat)    setSelectedCat(st.selectedCat);
    if (st?.selectedSubCat) setSelectedSubCat(st.selectedSubCat);
    if (st?.parts)          setParts(st.parts);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  /* set sensible defaults for add-part form once ref data loads */
  useEffect(() => {
    if (conditions.length && !newPartCondId) {
      const unk = conditions.find(c => c.code === 'UNKNOWN') || conditions[0];
      if (unk) setNewPartCondId(String(unk.refConditionId));
    }
  }, [conditions]); // eslint-disable-line

  useEffect(() => {
    if (statuses.length && !newPartStatId) {
      const def = statuses.find(s => s.code === 'IN_STOCK') || statuses[0];
      if (def) setNewPartStatId(String(def.refStatusId));
    }
  }, [statuses]); // eslint-disable-line

  /* ── Load categories whenever vehicle is set ─────────────────────── */
  const loadCategories = useCallback(async (vehicleId) => {
    setLoadingCats(true);
    setCatError('');
    try {
      const res = await getCategories(vehicleId);
      if (res.success && Array.isArray(res.data) && res.data.length) {
        const vehicleRows = res.data.filter(r => r.source === 'vehicle');
        setHierarchy(vehicleRows.length ? buildHierarchy(vehicleRows) : []);
      } else {
        setHierarchy([]);
      }
    } catch (err) {
      setCatError('Could not load categories.');
      setHierarchy([]);
    } finally {
      setLoadingCats(false);
    }
  }, []);

  useEffect(() => {
    if (vehicle?.vehicleId) {
      loadCategories(vehicle.vehicleId);
    }
  }, [vehicle, loadCategories]);

  /* ── Load parts when a sub-category is selected ──────────────────── */
  useEffect(() => {
    if (!selectedSubCat || !vehicle?.vehicleId) return;
    setLoadingParts(true);
    setPartsError('');
    setParts([]);
    listParts(vehicle.vehicleId, selectedSubCat.subCategoryId)
      .then(res => {
        if (res.success) setParts(res.data || []);
        else setPartsError('Could not load parts.');
      })
      .catch(() => setPartsError('Could not load parts.'))
      .finally(() => setLoadingParts(false));
  }, [selectedSubCat, vehicle]);

  /* ── Unknown view handlers ───────────────────────────────────────── */
  async function openUnknownView() {
    setViewingUnknown(true);
    setUnknownError('');

    if (!unknownPartId) {
      setLoadingUnknown(true);
      try {
        const res = await getUnknownPart(vehicle.vehicleId);
        if (!res.success) { setUnknownError('Could not load Unknown part.'); return; }
        const pid = res.data.partId;
        setUnknownPartId(pid);
        await loadUnknownImages(pid);
      } catch {
        setUnknownError('Could not load Unknown part.');
      } finally {
        setLoadingUnknown(false);
      }
    } else {
      await loadUnknownImages(unknownPartId);
    }
  }

  async function loadUnknownImages(partId) {
    setLoadingUnknown(true);
    try {
      const res = await listImages(partId);
      setUnknownImages(res.success ? (res.data || []) : []);
    } catch {
      setUnknownImages([]);
    } finally {
      setLoadingUnknown(false);
    }
  }

  function closeUnknownView() {
    setViewingUnknown(false);
    setAssignImage(null);
    setLightboxUrl(null);
  }

  async function handleDeleteUnknown(imageId) {
    if (!window.confirm('Delete this photo permanently? This cannot be undone.')) return;
    try {
      await deleteImage(unknownPartId, imageId);
      setUnknownImages(prev => prev.filter(i => i.partImageId !== imageId));
    } catch {
      alert('Could not delete the image. Please try again.');
    }
  }

  /* ── Assign modal handlers ───────────────────────────────────────── */
  function openAssignModal(img) {
    setAssignImage(img);
    setAssignCatId('');
    setAssignSubCatId('');
    setAssignParts([]);
    setAssignSelectedId(null);
    setShowAddPart(false);
    setNewPartName(''); setNewPartOem(''); setNewPartDesc('');
    setAddPartError('');
  }

  function closeAssignModal() {
    setAssignImage(null);
  }

  function handleAssignCatChange(catId) {
    setAssignCatId(catId);
    setAssignSubCatId('');
    setAssignParts([]);
    setAssignSelectedId(null);
    setShowAddPart(false);
    setAddPartError('');
  }

  async function handleAssignSubCatChange(subCatId) {
    setAssignSubCatId(subCatId);
    setAssignSelectedId(null);
    setShowAddPart(false);
    setAddPartError('');
    if (!subCatId) { setAssignParts([]); return; }
    setLoadingAssign(true);
    try {
      const res = await listParts(vehicle.vehicleId, subCatId);
      setAssignParts(res.success ? (res.data || []) : []);
    } catch {
      setAssignParts([]);
    } finally {
      setLoadingAssign(false);
    }
  }

  async function handleAssignImage() {
    if (!assignSelectedId || !assignImage) return;
    setAssignBusy(true);
    try {
      await reassignImage(unknownPartId, assignImage.partImageId, assignSelectedId);
      setUnknownImages(prev => prev.filter(i => i.partImageId !== assignImage.partImageId));
      setAssignImage(null);
    } catch {
      alert('Could not assign the image. Please try again.');
    } finally {
      setAssignBusy(false);
    }
  }

  async function handleCreateAndAssign(e) {
    e.preventDefault();
    if (!newPartName.trim()) { setAddPartError('Part name is required.'); return; }
    if (!newPartCondId)      { setAddPartError('Condition is required.'); return; }
    if (!newPartStatId)      { setAddPartError('Status is required.'); return; }
    setAddPartBusy(true);
    setAddPartError('');
    try {
      const res = await createPart({
        vehicleId:       vehicle.vehicleId,
        name:            newPartName.trim(),
        oem_part_number: newPartOem   || undefined,
        description:     newPartDesc  || undefined,
        refCategoryId:   parseInt(assignSubCatId),
        refConditionId:  parseInt(newPartCondId),
        refStatusId:     parseInt(newPartStatId),
      });
      if (!res.success) { setAddPartError(res.message || 'Failed to create part.'); return; }
      const newId = res.data.partId;
      await reassignImage(unknownPartId, assignImage.partImageId, newId);
      setUnknownImages(prev => prev.filter(i => i.partImageId !== assignImage.partImageId));
      setAssignImage(null);
    } catch (err) {
      setAddPartError(err?.response?.data?.message || 'Failed to create part. Check server logs.');
    } finally {
      setAddPartBusy(false);
    }
  }

  /* ── AI generate handler ─────────────────────────────────────────── */
  async function handleAiGenerate() {
    if (!vehicle?.vehicleId) return;
    setGenerating(true);
    setGenError('');
    try {
      const res = await generateCategories(vehicle.vehicleId);
      if (res.success) {
        setShowAiModal(false);
        await loadCategories(vehicle.vehicleId);
      } else {
        setGenError(res.message || 'AI generation failed.');
      }
    } catch (err) {
      setGenError(err?.response?.data?.message || 'AI generation failed. Check the server.');
    } finally {
      setGenerating(false);
    }
  }

  /* ── AI generate parts for a sub-system ─────────────────────────── */
  function openPartsModal(cat, sub, e) {
    e?.stopPropagation();
    setPartsModalTarget({ category: cat, sub_category: sub });
    setGenPartsError('');
    setShowPartsModal(true);
  }

  async function handleGenerateParts() {
    if (!vehicle || !partsModalTarget) return;
    setGeneratingParts(true);
    setGenPartsError('');
    try {
      const payload = {
        year:         vehicle.year,
        make:         vehicle.make,
        model:        vehicle.model,
        variant:      vehicle.trim_package   || undefined,
        engine:       vehicle.engine_desc    || undefined,
        category:     partsModalTarget.category,
        sub_category: partsModalTarget.sub_category,
      };
      const res = await generateSubSystemParts(vehicle.vehicleId, payload);
      if (res.success) {
        setShowPartsModal(false);
        if (selectedSubCat?.sub_category === partsModalTarget.sub_category) {
          const refreshed = await listParts(vehicle.vehicleId, selectedSubCat.subCategoryId);
          if (refreshed.success) setParts(refreshed.data || []);
        }
      } else {
        setGenPartsError(res.message || 'Parts generation failed.');
      }
    } catch (err) {
      setGenPartsError(err?.response?.data?.message || 'Parts generation failed. Check the server.');
    } finally {
      setGeneratingParts(false);
    }
  }

  /* ── Navigation helpers ──────────────────────────────────────────── */
  function goHome()         { navigate('/dashboard'); }
  function handleSignOut()  { signOut(); navigate('/'); }

  function selectCategory(cat) {
    setSelectedCat(cat);
    setSelectedSubCat(null);
    setParts([]);
  }

  function selectSubCategory(sub) {
    setSelectedSubCat(sub);
    setParts([]);
  }

  function goBackToCats() {
    setSelectedCat(null);
    setSelectedSubCat(null);
    setParts([]);
  }

  function goBackToSubCats() {
    setSelectedSubCat(null);
    setParts([]);
  }

  /* ── Derived state ───────────────────────────────────────────────── */
  const vehicleLabel = vehicle
    ? `${vehicle.year} ${vehicle.make} ${vehicle.model}${vehicle.trim_package ? ' ' + vehicle.trim_package : ''}`.trim()
    : '—';

  const level = selectedSubCat ? 2 : selectedCat ? 1 : 0;

  /* sub-categories list for the assign modal category picker */
  const assignSubCats = assignCatId
    ? (hierarchy.find(c => String(c.categoryId) === String(assignCatId))?.sub_categories || [])
    : [];

  /* ── Render ──────────────────────────────────────────────────────── */
  return (
    <div className="pts-root">

      {/* ── Header ──────────────────────────────────────────────────── */}
      <header className="pts-header">
        <div className="pts-header-brand">
          <span className="pts-header-gear">⚙</span>
          <span className="pts-header-name">AutoRestoration</span>
        </div>

        <div className="pts-header-vehicle">
          {vehicle && (
            <>
              <span className="pts-vehicle-icon">🚗</span>
              <span className="pts-vehicle-label">{vehicleLabel}</span>
              {vehicle.nickname && (
                <span className="pts-vehicle-nick">"{vehicle.nickname}"</span>
              )}
            </>
          )}
        </div>

        <div className="pts-header-user">
          <span className="pts-username">@{user?.username}</span>
          <button className="pts-signout-btn" onClick={handleSignOut}>Sign Out</button>
        </div>
      </header>

      {/* ── Page body ───────────────────────────────────────────────── */}
      <div className="pts-body">

        {/* ── Back to dashboard ──────────────────────────────────── */}
        <button className="pts-back-dash" onClick={goHome}>
          ← Dashboard
        </button>

        {/* ── Page title + breadcrumb ─────────────────────────────── */}
        <div className="pts-page-title-row">
          <div className="pts-breadcrumb">
            <span
              className={`pts-crumb ${(level === 0 && !viewingUnknown) ? 'pts-crumb-active' : 'pts-crumb-link'}`}
              onClick={() => { goBackToCats(); closeUnknownView(); }}
            >
              🔩 Parts
            </span>

            {viewingUnknown && (
              <>
                <span className="pts-crumb-sep">›</span>
                <span className="pts-crumb pts-crumb-active">📷 Unknown Parts</span>
              </>
            )}

            {!viewingUnknown && selectedCat && (
              <>
                <span className="pts-crumb-sep">›</span>
                <span
                  className={`pts-crumb ${level === 1 ? 'pts-crumb-active' : 'pts-crumb-link'}`}
                  onClick={level > 1 ? goBackToSubCats : undefined}
                >
                  {selectedCat.category}
                </span>
              </>
            )}
            {!viewingUnknown && selectedSubCat && (
              <>
                <span className="pts-crumb-sep">›</span>
                <span className="pts-crumb pts-crumb-active">
                  {selectedSubCat.sub_category}
                </span>
              </>
            )}
          </div>

          {/* AI Generate button — only on the root level */}
          {level === 0 && !viewingUnknown && !loadingCats && (
            <button
              className="pts-ai-btn"
              onClick={() => { setGenError(''); setShowAiModal(true); }}
            >
              🤖 AI Generate Categories
            </button>
          )}
        </div>

        {/* ═══════════════════════════════════════════════════════════ */}
        {/* UNKNOWN / UNIDENTIFIED PHOTOS VIEW                         */}
        {/* ═══════════════════════════════════════════════════════════ */}
        {viewingUnknown && (
          <>
            <button className="pts-level-back" onClick={closeUnknownView}>
              ← Back to Categories
            </button>

            <div className="pts-unknown-header">
              <h3 className="pts-unknown-title">📷 Unidentified Photos</h3>
              <p className="pts-unknown-subtitle">
                Photos taken before the part was identified. Assign each photo
                to the right part, or delete it if it's no longer needed.
              </p>
            </div>

            {loadingUnknown && (
              <div className="pts-loading">Loading photos…</div>
            )}

            {unknownError && (
              <div className="pts-error">{unknownError}</div>
            )}

            {!loadingUnknown && !unknownError && unknownImages.length === 0 && (
              <div className="pts-empty">
                <div className="pts-empty-icon">📷</div>
                <h3 className="pts-empty-title">No unidentified photos</h3>
                <p className="pts-empty-text">
                  All photos have been assigned to parts. Great job!
                </p>
              </div>
            )}

            {!loadingUnknown && unknownImages.length > 0 && (
              <div className="pts-unknown-grid">
                {unknownImages.map(img => (
                  <div key={img.partImageId} className="pts-unknown-card">
                    <div
                      className="pts-unknown-thumb"
                      onClick={() => setLightboxUrl(img.url)}
                      title="Click to enlarge"
                    >
                      {img.url ? (
                        <img src={img.url} alt={img.original_filename || 'Part photo'} />
                      ) : (
                        <div className="pts-unknown-noimg">📷</div>
                      )}
                    </div>
                    {img.caption && (
                      <div className="pts-unknown-caption">{img.caption}</div>
                    )}
                    <div className="pts-unknown-actions">
                      <button
                        className="pts-unknown-del-btn"
                        onClick={() => handleDeleteUnknown(img.partImageId)}
                        title="Delete this photo"
                      >
                        🗑 Delete
                      </button>
                      <button
                        className="pts-unknown-assign-btn"
                        onClick={() => openAssignModal(img)}
                        title="Assign to a part"
                      >
                        📌 Assign
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </>
        )}

        {/* ═══════════════════════════════════════════════════════════ */}
        {/* LEVEL 0: Category grid                                      */}
        {/* ═══════════════════════════════════════════════════════════ */}
        {!viewingUnknown && level === 0 && (
          <>
            {loadingCats && (
              <div className="pts-loading">Loading categories…</div>
            )}

            {!loadingCats && catError && (
              <div className="pts-error">{catError}</div>
            )}

            {!loadingCats && !catError && hierarchy.length === 0 && (
              <div className="pts-empty">
                <div className="pts-empty-icon">🔩</div>
                <h3 className="pts-empty-title">No categories yet</h3>
                <p className="pts-empty-text">
                  Use the AI Generate button to build a restoration category
                  hierarchy tailored to your {vehicleLabel}.
                </p>
                <button
                  className="pts-ai-btn pts-ai-btn-large"
                  onClick={() => { setGenError(''); setShowAiModal(true); }}
                >
                  🤖 AI Generate Categories
                </button>
              </div>
            )}

            {/* Always show the grid once loading is done (even empty — Unknown card is always there) */}
            {!loadingCats && vehicle && (
              <div className="pts-cat-grid">

                {/* ── Unknown / Unidentified Photos — always first ── */}
                <button
                  className="pts-cat-card pts-cat-unknown-card"
                  onClick={openUnknownView}
                >
                  <span className="pts-cat-icon">📷</span>
                  <span className="pts-cat-name">Unknown Parts</span>
                  <div className="pts-cat-counts">
                    <span className="pts-cat-sub-count">Unidentified photos</span>
                  </div>
                  <span className="pts-cat-arrow">›</span>
                </button>

                {/* ── Normal categories ───────────────────────────── */}
                {hierarchy.map(cat => (
                  <button
                    key={cat.categoryId}
                    className="pts-cat-card"
                    onClick={() => selectCategory(cat)}
                  >
                    <span className="pts-cat-icon">{categoryIcon(cat.category)}</span>
                    <span className="pts-cat-name">{cat.category}</span>
                    <div className="pts-cat-counts">
                      <span className="pts-cat-sub-count">
                        {cat.sub_categories.length} sub-system{cat.sub_categories.length !== 1 ? 's' : ''}
                      </span>
                      {cat.part_count > 0 && (
                        <span className="pts-cat-part-count">
                          {cat.part_count} part{cat.part_count !== 1 ? 's' : ''}
                        </span>
                      )}
                    </div>
                    <span className="pts-cat-arrow">›</span>
                  </button>
                ))}
              </div>
            )}
          </>
        )}

        {/* ═══════════════════════════════════════════════════════════ */}
        {/* LEVEL 1: Sub-category grid                                  */}
        {/* ═══════════════════════════════════════════════════════════ */}
        {!viewingUnknown && level === 1 && selectedCat && (
          <>
            <button className="pts-level-back" onClick={goBackToCats}>
              ← Back to Categories
            </button>

            {selectedCat.sub_categories.length === 0 ? (
              <div className="pts-empty">
                <div className="pts-empty-icon">📂</div>
                <h3 className="pts-empty-title">No sub-categories</h3>
                <p className="pts-empty-text">
                  This category has no sub-systems defined yet.
                </p>
              </div>
            ) : (
              <div className="pts-subcat-grid">
                {selectedCat.sub_categories.map(sub => (
                  <button
                    key={sub.subCategoryId}
                    className="pts-subcat-card"
                    onClick={() => selectSubCategory(sub)}
                  >
                    <span className="pts-subcat-name">{sub.sub_category}</span>
                    <span className="pts-subcat-actions">
                      {sub.part_count > 0 ? (
                        <span className="pts-subcat-part-count">
                          {sub.part_count} part{sub.part_count !== 1 ? 's' : ''}
                        </span>
                      ) : (
                        <span
                          className="pts-subcat-ai-btn"
                          title={`Generate parts for ${sub.sub_category}`}
                          onClick={(e) => openPartsModal(selectedCat.category, sub.sub_category, e)}
                        >
                          🤖
                        </span>
                      )}
                      <span className="pts-subcat-arrow">›</span>
                    </span>
                  </button>
                ))}
              </div>
            )}
          </>
        )}

        {/* ═══════════════════════════════════════════════════════════ */}
        {/* LEVEL 2: Parts list                                         */}
        {/* ═══════════════════════════════════════════════════════════ */}
        {!viewingUnknown && level === 2 && selectedSubCat && (
          <>
            <button className="pts-level-back" onClick={goBackToSubCats}>
              ← Back to {selectedCat?.category}
            </button>

            {/* AI button — always visible at the top of the parts view */}
            <div className="pts-parts-ai-bar">
              <div className="pts-parts-ai-bar-label">
                <span className="pts-parts-ai-bar-path">
                  {selectedCat?.category} › {selectedSubCat.sub_category}
                </span>
                <span className="pts-parts-ai-bar-hint">
                  AI generates parts scoped to this sub-system only
                </span>
              </div>
              <button
                className="pts-ai-btn"
                onClick={() => openPartsModal(selectedCat?.category, selectedSubCat.sub_category)}
              >
                🤖 Generate Parts
              </button>
            </div>

            {loadingParts && (
              <div className="pts-loading">Loading parts…</div>
            )}

            {!loadingParts && partsError && (
              <div className="pts-error">{partsError}</div>
            )}

            {!loadingParts && !partsError && parts.length === 0 && (
              <div className="pts-empty">
                <div className="pts-empty-icon">📦</div>
                <h3 className="pts-empty-title">No parts yet</h3>
                <p className="pts-empty-text">
                  No parts have been added to{' '}
                  <strong>{selectedSubCat.sub_category}</strong> yet.
                  Hit <strong>Generate Parts</strong> above to have Claude build
                  the list for this sub-system.
                </p>
              </div>
            )}

            {!loadingParts && parts.length > 0 && (
              <div className="pts-parts-section">
                <div className="pts-parts-header-row">
                  <span className="pts-parts-count">
                    {parts.length} part{parts.length !== 1 ? 's' : ''}
                  </span>
                </div>
                <div className="pts-parts-list">
                  {parts.map(p => (
                    <div
                      key={p.partId}
                      className="pts-part-card pts-part-card-clickable"
                      onClick={() => navigate(`/parts/${p.partId}`, {
                        state: { selectedCat, selectedSubCat, parts }
                      })}
                    >
                      <div className="pts-part-main">
                        <span className="pts-part-name">{p.name}</span>
                        {p.oem_part_number && (
                          <span className="pts-part-oem">OEM# {p.oem_part_number}</span>
                        )}
                        <span className="pts-part-detail-hint">›</span>
                      </div>
                      <div className="pts-part-meta">
                        {p.condition_code && (
                          <span
                            className="pts-part-condition"
                            style={{ background: CONDITION_COLORS[p.condition_code] || CONDITION_COLORS.UNKNOWN }}
                          >
                            {p.condition_code}
                          </span>
                        )}
                        <select
                          className="pts-part-status-sel"
                          value={p.status_code || ''}
                          onClick={e => e.stopPropagation()}
                          onChange={async e => {
                            const code = e.target.value;
                            e.stopPropagation();
                            setParts(prev => prev.map(x =>
                              x.partId === p.partId ? { ...x, status_code: code } : x
                            ));
                            try { await patchPart(p.partId, { status_code: code }); }
                            catch { /* silent — optimistic update already applied */ }
                          }}
                        >
                          {statuses.map(s => (
                            <option key={s.refStatusId} value={s.code}>{s.name}</option>
                          ))}
                        </select>
                        {p.needs_restoration === 1 && (
                          <span className="pts-part-flag">🔧 Needs Work</span>
                        )}
                        {p.quantity > 1 && (
                          <span className="pts-part-flag">×{p.quantity}</span>
                        )}
                        {p.restoration_notes && (
                          <span
                            className="pts-part-alert"
                            title="Has restoration notes"
                            onClick={e => e.stopPropagation()}
                          >
                            !
                          </span>
                        )}
                        {(p.has_research === 1 || p.has_research === true) && (
                          <span
                            className="pts-part-alert pts-part-alert-research"
                            title="Has AI research data"
                            onClick={e => e.stopPropagation()}
                          >
                            !
                          </span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* ═══════════════════════════════════════════════════════════════ */}
      {/* LIGHTBOX                                                        */}
      {/* ═══════════════════════════════════════════════════════════════ */}
      {lightboxUrl && (
        <div className="pts-lightbox" onClick={() => setLightboxUrl(null)}>
          <img src={lightboxUrl} alt="Part" className="pts-lightbox-img" />
          <button className="pts-lightbox-close" onClick={() => setLightboxUrl(null)}>✕ Close</button>
        </div>
      )}

      {/* ═══════════════════════════════════════════════════════════════ */}
      {/* ASSIGN IMAGE MODAL                                              */}
      {/* ═══════════════════════════════════════════════════════════════ */}
      {assignImage && (
        <div className="pts-modal-overlay" onClick={closeAssignModal}>
          <div className="pts-assign-modal" onClick={e => e.stopPropagation()}>

            <h2 className="pts-assign-title">📌 Assign Photo to Part</h2>

            {/* Thumbnail preview */}
            {assignImage.url && (
              <div className="pts-assign-preview">
                <img src={assignImage.url} alt="Photo to assign" />
              </div>
            )}

            {/* ── Cascading selects ─────────────────────────────── */}
            <div className="pts-assign-selects">

              {/* Category */}
              <div className="pts-assign-field">
                <label className="pts-assign-label">Category</label>
                <select
                  className="pts-assign-select"
                  value={assignCatId}
                  onChange={e => handleAssignCatChange(e.target.value)}
                >
                  <option value="">— Select Category —</option>
                  {hierarchy.map(c => (
                    <option key={c.categoryId} value={c.categoryId}>{c.category}</option>
                  ))}
                </select>
              </div>

              {/* Sub-category */}
              {assignCatId && (
                <div className="pts-assign-field">
                  <label className="pts-assign-label">Sub-system</label>
                  <select
                    className="pts-assign-select"
                    value={assignSubCatId}
                    onChange={e => handleAssignSubCatChange(e.target.value)}
                  >
                    <option value="">— Select Sub-system —</option>
                    {assignSubCats.map(s => (
                      <option key={s.subCategoryId} value={s.subCategoryId}>{s.sub_category}</option>
                    ))}
                  </select>
                </div>
              )}
            </div>

            {/* ── Parts list ────────────────────────────────────── */}
            {assignSubCatId && !showAddPart && (
              <div className="pts-assign-parts-section">
                <div className="pts-assign-parts-header">
                  <span className="pts-assign-parts-label">Select Part</span>
                  <button
                    className="pts-assign-addpart-btn"
                    onClick={() => { setShowAddPart(true); setAssignSelectedId(null); setAddPartError(''); }}
                  >
                    + Add Part
                  </button>
                </div>

                {loadingAssign ? (
                  <div className="pts-assign-loading">Loading parts…</div>
                ) : assignParts.length === 0 ? (
                  <div className="pts-assign-empty">
                    No parts in this sub-system yet — use <strong>Add Part</strong> to create one.
                  </div>
                ) : (
                  <div className="pts-assign-parts-list">
                    {assignParts.map(p => (
                      <button
                        key={p.partId}
                        className={`pts-assign-part-row${assignSelectedId === p.partId ? ' pts-assign-part-row-sel' : ''}`}
                        onClick={() => setAssignSelectedId(p.partId)}
                      >
                        <span className="pts-assign-part-name">{p.name}</span>
                        {p.oem_part_number && (
                          <span className="pts-assign-part-oem">OEM# {p.oem_part_number}</span>
                        )}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* ── Add Part form ─────────────────────────────────── */}
            {assignSubCatId && showAddPart && (
              <form className="pts-addpart-form" onSubmit={handleCreateAndAssign}>
                <div className="pts-addpart-header">
                  <span className="pts-addpart-title">➕ New Part</span>
                  <button
                    type="button"
                    className="pts-addpart-cancel-link"
                    onClick={() => { setShowAddPart(false); setAddPartError(''); }}
                  >
                    ← Back to list
                  </button>
                </div>

                <input
                  className="pts-addpart-input"
                  placeholder="Part Name *"
                  value={newPartName}
                  onChange={e => setNewPartName(e.target.value)}
                  required
                />
                <input
                  className="pts-addpart-input"
                  placeholder="OEM Part # (optional)"
                  value={newPartOem}
                  onChange={e => setNewPartOem(e.target.value)}
                />
                <textarea
                  className="pts-addpart-textarea"
                  placeholder="Description (optional)"
                  value={newPartDesc}
                  onChange={e => setNewPartDesc(e.target.value)}
                  rows={2}
                />

                <div className="pts-addpart-row2">
                  <div className="pts-addpart-field">
                    <label className="pts-addpart-label">Condition</label>
                    <select
                      className="pts-addpart-select"
                      value={newPartCondId}
                      onChange={e => setNewPartCondId(e.target.value)}
                    >
                      <option value="">— Select —</option>
                      {conditions.map(c => (
                        <option key={c.refConditionId} value={c.refConditionId}>{c.name}</option>
                      ))}
                    </select>
                  </div>
                  <div className="pts-addpart-field">
                    <label className="pts-addpart-label">Status</label>
                    <select
                      className="pts-addpart-select"
                      value={newPartStatId}
                      onChange={e => setNewPartStatId(e.target.value)}
                    >
                      <option value="">— Select —</option>
                      {statuses.map(s => (
                        <option key={s.refStatusId} value={s.refStatusId}>{s.name}</option>
                      ))}
                    </select>
                  </div>
                </div>

                {addPartError && (
                  <div className="pts-addpart-error">{addPartError}</div>
                )}

                <div className="pts-addpart-actions">
                  <button type="submit" className="pts-addpart-submit" disabled={addPartBusy}>
                    {addPartBusy ? 'Creating…' : '✓ Add Part & Assign Photo'}
                  </button>
                </div>
              </form>
            )}

            {/* ── Modal footer ──────────────────────────────────── */}
            <div className="pts-assign-footer">
              {assignSelectedId && !showAddPart && (
                <button
                  className="pts-assign-confirm-btn"
                  onClick={handleAssignImage}
                  disabled={assignBusy}
                >
                  {assignBusy ? 'Assigning…' : '✓ Assign to Selected Part'}
                </button>
              )}
              <button className="pts-assign-cancel-btn" onClick={closeAssignModal}>
                Cancel
              </button>
            </div>

          </div>
        </div>
      )}

      {/* ═══════════════════════════════════════════════════════════════ */}
      {/* AI PARTS MODAL (sub-system)                                    */}
      {/* ═══════════════════════════════════════════════════════════════ */}
      {showPartsModal && partsModalTarget && (
        <div className="pts-modal-overlay" onClick={() => !generatingParts && setShowPartsModal(false)}>
          <div className="pts-modal" onClick={e => e.stopPropagation()}>

            <div className="pts-modal-icon">🔩</div>
            <h2 className="pts-modal-title">Generate Parts — {partsModalTarget.sub_category}</h2>

            <div className="pts-modal-body">
              <p>
                Claude will generate a complete parts list specifically for the{' '}
                <strong>{partsModalTarget.category} &rsaquo; {partsModalTarget.sub_category}</strong>{' '}
                sub-system of your <strong>{vehicleLabel}</strong>.
              </p>
              <ul className="pts-modal-list">
                <li>🔩 Every individual part, seal, gasket, and fastener set</li>
                <li>🏷️ OEM part numbers where known</li>
                <li>📂 Scoped to this sub-system only — no unrelated parts</li>
                <li>⚡ Much faster and smaller than a full-vehicle generation</li>
              </ul>
              <p className="pts-modal-note">
                Parts will be added to your inventory. Existing parts with matching
                names are updated, not duplicated.
              </p>
            </div>

            {genPartsError && (
              <div className="pts-modal-error">{genPartsError}</div>
            )}

            <div className="pts-modal-actions">
              <button
                className="pts-modal-cancel"
                onClick={() => setShowPartsModal(false)}
                disabled={generatingParts}
              >
                ← Back to Parts
              </button>
              <button
                className="pts-modal-confirm"
                onClick={handleGenerateParts}
                disabled={generatingParts}
              >
                {generatingParts ? (
                  <><span className="pts-modal-spinner" /> Generating…</>
                ) : (
                  '🤖 Continue'
                )}
              </button>
            </div>

          </div>
        </div>
      )}

      {/* ═══════════════════════════════════════════════════════════════ */}
      {/* AI CATEGORIES MODAL                                             */}
      {/* ═══════════════════════════════════════════════════════════════ */}
      {showAiModal && (
        <div className="pts-modal-overlay" onClick={() => !generating && setShowAiModal(false)}>
          <div className="pts-modal" onClick={e => e.stopPropagation()}>

            <div className="pts-modal-icon">🤖</div>
            <h2 className="pts-modal-title">AI Category Generation</h2>

            <div className="pts-modal-body">
              <p>
                Claude will analyze your <strong>{vehicleLabel}</strong> and
                build a complete parts category hierarchy tailored to this
                exact vehicle — covering every system needed for a full
                frame-off restoration.
              </p>
              <ul className="pts-modal-list">
                <li>🔧 Top-level systems (Engine, Drivetrain, Body, Interior, etc.)</li>
                <li>📂 Sub-systems specific to your trim and engine</li>
                <li>⚡ Correct restoration terminology for the year and make</li>
              </ul>
              <p className="pts-modal-note">
                This typically takes 5–15 seconds and uses a small amount of
                your AI token allowance. Existing categories will be updated,
                not deleted.
              </p>
            </div>

            {genError && (
              <div className="pts-modal-error">{genError}</div>
            )}

            <div className="pts-modal-actions">
              <button
                className="pts-modal-cancel"
                onClick={() => setShowAiModal(false)}
                disabled={generating}
              >
                ← Back to Parts
              </button>
              <button
                className="pts-modal-confirm"
                onClick={handleAiGenerate}
                disabled={generating}
              >
                {generating ? (
                  <><span className="pts-modal-spinner" /> Generating…</>
                ) : (
                  '🤖 Continue'
                )}
              </button>
            </div>

          </div>
        </div>
      )}

    </div>
  );
}
